// =============================================================================
// @tnb/session-status — Canonical sub-session status hierarchy
// Single source of truth for determining parent session status
// from its sub-sessions' statuses.
//
// Hierarchy (most blocking first):
//   active(1) < paused(2) < ready(3) < received(4) < submitted(5) < pending(6) < cancelled(7) < completed(8)
//
// "active" ranks first because if any sub-session is running, the parent
// session is active. "paused" ranks next because paused work still needs
// attention before the session can complete.
//
// Status semantics (post WCO-2146):
//   pending   = session created, transfer order not yet submitted to warehouse
//   submitted = transfer order submitted, awaiting cases to physically arrive
//   received  = transfer cases scanned and verified at production facility
//   ready     = first in queue + materials available + station free
//
// Scope: STATUS_TO_RANK_SQL / STATUS_RANK apply only to this canonical hierarchy.
// Do NOT use them where a query needs a different ordering — e.g. the operator
// clock-in UPCOMING-WORK fallback (pending<received<completed<cancelled), which uses
// TERMINAL_FALLBACK_RANK_SQL (clockInOut.ts / stationScan.ts) — not STATUS_TO_RANK_SQL.
//
// WCO-2750: station overview is NOT such an exception. Its old
// active<ready<paused<received<pending ordering was a pre-shared-package legacy
// artifact, not a deliberate station-specific rule, and overview.ts now uses the
// canonical rank. A station holding both a paused and a ready sub-session is
// physically occupied by the paused work — paused must outrank ready.
// =============================================================================

'use strict';

const STATUS_RANK = Object.freeze({
  'active': 1,
  'paused': 2,
  'ready': 3,
  'received': 4,
  'submitted': 5,
  'pending': 6,
  'cancelled': 7,
  'completed': 8,
});

const RANK_TO_STATUS = Object.freeze({
  1: 'active',
  2: 'paused',
  3: 'ready',
  4: 'received',
  5: 'submitted',
  6: 'pending',
  7: 'cancelled',
  8: 'completed',
});

/**
 * SQL CASE expression that converts a status column to its numeric rank.
 * Usage: `SELECT CASE MIN(${STATUS_TO_RANK_SQL('status')}) ${RANK_TO_STATUS_SQL} END`
 * @param {string} [col='status'] - the column name holding the status value
 * @returns {string} SQL CASE fragment
 */
function STATUS_TO_RANK_SQL(col) {
  col = col || 'status';
  return `CASE ${col} WHEN 'active' THEN 1 WHEN 'paused' THEN 2 WHEN 'ready' THEN 3 WHEN 'received' THEN 4 WHEN 'submitted' THEN 5 WHEN 'pending' THEN 6 WHEN 'cancelled' THEN 7 WHEN 'completed' THEN 8 END`;
}

/**
 * SQL CASE for operator clock-in terminal fallback ORDER BY (pending → received → completed → cancelled).
 * NOT the canonical rank — do not substitute STATUS_TO_RANK_SQL.
 * @param {string} [col='status'] - the column name holding the status value
 * @returns {string} SQL CASE fragment
 */
function TERMINAL_FALLBACK_RANK_SQL(col) {
  col = col || 'status';
  return `
    CASE ${col}
      WHEN 'pending' THEN 1
      WHEN 'received' THEN 2
      WHEN 'completed' THEN 3
      WHEN 'cancelled' THEN 4
    END
  `;
}

/**
 * SQL CASE expression that converts a numeric rank back to a status string.
 * Wrap with: `CASE MIN(...) ${RANK_TO_STATUS_SQL} END`
 */
const RANK_TO_STATUS_SQL =
  "WHEN 1 THEN 'active' WHEN 2 THEN 'paused' WHEN 3 THEN 'ready' WHEN 4 THEN 'received' WHEN 5 THEN 'submitted' WHEN 6 THEN 'pending' WHEN 7 THEN 'cancelled' WHEN 8 THEN 'completed'";

/**
 * Compute the display_status for a parent session from its sub-sessions.
 * Returns the status with the lowest rank (most blocking).
 * @param {Array<{status: string}>} subSessions
 * @param {string} [fallbackStatus] - status to use if subSessions is empty
 * @returns {string} the resolved display_status
 */
function resolveDisplayStatus(subSessions, fallbackStatus) {
  if (!subSessions || subSessions.length === 0) {
    return fallbackStatus || 'pending';
  }
  const minRank = Math.min(
    ...subSessions.map(s => STATUS_RANK[s.status] || 8)
  );
  return RANK_TO_STATUS[minRank] || fallbackStatus || 'pending';
}

/**
 * Statuses a production session may still be cancelled from (WCO-2706).
 *
 * This is the complete set allowed by the chk_session_status / chk_sub_session_status CHECK
 * constraints MINUS the four where cancelling is unsafe or meaningless: 'active' and 'paused'
 * (work is under way — cancelling would strand operators mid-run) and 'completed' / 'cancelled'
 * (terminal). 'planned' is the legacy alias for 'received' on rows predating WCO-2146.
 *
 * Enforced server-side in the cancel route, and the UI now gates its cancel buttons on THIS list
 * too (via admin-frontend `utils/sessionStatus.ts`), so the two cannot drift. The route remains
 * the authority regardless: it re-checks under FOR UPDATE and rejects anything a client wrongly
 * lets through.
 */
const CANCELLABLE_STATUSES = Object.freeze([
  'pending', 'submitted', 'received', 'planned', 'ready',
]);

/**
 * True when a session in this status may still be cancelled.
 * @param {string|null|undefined} status
 * @returns {boolean}
 */
function isSessionCancellable(status) {
  return CANCELLABLE_STATUSES.includes(String(status == null ? '' : status));
}

/**
 * SQL fragment that computes session elapsed seconds (wall clock minus pause time).
 * Joins production_sub_session (start/end) with production_session_paused (pause records).
 *
 * @param {string} sessionNumberExpr - SQL expression for the session_number column
 *   (e.g. 'ps.session_number')
 * @returns {string} SQL expression that evaluates to elapsed seconds (integer, >= 0)
 *
 * Usage:
 *   `SELECT ${SESSION_ELAPSED_SQL('ps.session_number')} as elapsed_seconds FROM ...`
 */
function SESSION_ELAPSED_SQL(sessionNumberExpr) {
  sessionNumberExpr = sessionNumberExpr || 'ps.session_number';
  // Floor wall clock first, subtract pause seconds (may be fractional), then floor the
  // final difference before ::integer — Postgres ::integer rounds; Yield Variance floors.
  return `FLOOR(GREATEST(
    COALESCE(
      FLOOR(EXTRACT(EPOCH FROM (
        MAX(COALESCE(pss.completed_at, NOW())) - MIN(pss.started_at)
      ))) -
      COALESCE((
        SELECT SUM(EXTRACT(EPOCH FROM (COALESCE(psp.pause_end, CURRENT_TIMESTAMP) - psp.pause_start)))
        FROM ics.hte_production_session_paused psp
        WHERE psp.session_number = ${sessionNumberExpr}
      ), 0),
      0
    ),
    0
  ))::integer`;
}

module.exports = {
  STATUS_RANK,
  RANK_TO_STATUS,
  STATUS_TO_RANK_SQL,
  TERMINAL_FALLBACK_RANK_SQL,
  RANK_TO_STATUS_SQL,
  resolveDisplayStatus,
  SESSION_ELAPSED_SQL,
  CANCELLABLE_STATUSES,
  isSessionCancellable,
};
